import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaTachometerAlt,
  FaUsers,
  FaBullseye,
  FaList,
  FaFileAlt,
  FaUser,
  FaBox,
  FaSignOutAlt,
  FaTimes,
  FaCar,
  FaCity,
  FaRoute,
  FaChevronDown,
  FaBoxOpen,
  FaBoxes,
  FaWarehouse,
  FaUserEdit,
  FaMapSigns,
  FaTruckLoading,
} from "react-icons/fa";
import { GrUserWorker } from "react-icons/gr";
import { FaBoxesPacking } from "react-icons/fa6";
import { IoDocumentLockSharp } from "react-icons/io5";
import { logout } from "../../api/auth";
import { getUserById, getCompanies } from "../../api/user";
import { getToken } from "../../api/auth";
import Logo from "../../assets/images/katroriLogo.png";
import "./Sidebar.css";

const getUsernameFromToken = () => {
  const token = getToken();
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload.unique_name || payload.sub || null;
  } catch (err) {
    console.error("Failed to decode token:", err);
    return null;
  }
};

const Sidebar = ({ isOpen = false, onClose, isMobile }) => {
  const navigate = useNavigate();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isOrdersDropdownOpen, setIsOrdersDropdownOpen] = useState(false);
  const [isAgentsDropdownOpen, setIsAgentsDropdownOpen] = useState(false);
  const [companyName, setCompanyName] = useState("Company Name");
  const [isLoadingCompany, setIsLoadingCompany] = useState(true);

  const handleNavClick = () => {
    if (isMobile && onClose) {
      onClose();
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
    handleNavClick();
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const toggleOrdersDropdown = () => {
    setIsOrdersDropdownOpen(!isOrdersDropdownOpen);
  };

  const toggleAgentsDropdown = () => {
    setIsAgentsDropdownOpen(!isAgentsDropdownOpen);
  };

  useEffect(() => {
    const fetchCompanyName = async () => {
      const username = getUsernameFromToken();
      if (!username) {
        setIsLoadingCompany(false);
        return;
      }

      try {
        // Fetch user data
        const userData = await getUserById(username);

        // Fetch companies
        const companyData = await getCompanies();

        // Find matching company
        const matchingCompany = companyData.find(
          (company) => company.companyCode === userData.companyCode
        );

        if (matchingCompany) {
          setCompanyName(matchingCompany.companyName);
        }
      } catch (err) {
        console.error("Failed to fetch company name:", err);
      } finally {
        setIsLoadingCompany(false);
      }
    };

    fetchCompanyName();
  }, []);

  return (
    <aside
      className={`sidebar ${isOpen ? "open" : ""} ${isMobile ? "mobile" : ""}`}
    >
      <div className="sidebar-header">
        <img src={Logo} alt="WMS Logo" className="sidebar-logo" />
        {isMobile && (
          <button
            className="close-btn glass-btn"
            onClick={onClose}
            aria-label="Close menu"
          >
            <FaTimes size={20} />
          </button>
        )}
      </div>
      <nav className="nav-links">
        <Link className="nav-link" to="/dashboard" onClick={handleNavClick}>
          <span className="nav-icon">
            <FaTachometerAlt size={20} />
          </span>
          <span className="nav-text">Dashboard</span>
        </Link>

        {/* Agents, Controllers, Targets Dropdown */}
        <div className="nav-link dropdown" onClick={toggleAgentsDropdown}>
          <span className="nav-icon">
            <FaUsers size={20} />
          </span>
          <span className="nav-text">Users</span>
          <span className="nav-icon dropdown-icon">
            <FaChevronDown
              size={16}
              className={isAgentsDropdownOpen ? "rotate" : ""}
            />
          </span>
        </div>
        {isAgentsDropdownOpen && (
          <div className="dropdown-menu">
            <Link
              className="nav-link dropdown-item"
              to="/agents"
              onClick={handleNavClick}
            >
              <span className="nav-icon">
                <GrUserWorker size={20} />
              </span>
              <span className="nav-text">Agents</span>
            </Link>
            <Link
              className="nav-link dropdown-item"
              to="/controllers"
              onClick={handleNavClick}
            >
              <span className="nav-icon">
                <FaUserEdit size={20} />
              </span>
              <span className="nav-text">Controllers</span>
            </Link>
            <Link
              className="nav-link dropdown-item"
              to="/targets"
              onClick={handleNavClick}
            >
              <span className="nav-icon">
                <FaBullseye size={20} />
              </span>
              <span className="nav-text">Targets</span>
            </Link>
          </div>
        )}

        <Link
          className="nav-link"
          to="/document-access"
          onClick={handleNavClick}
        >
          <span className="nav-icon">
            <IoDocumentLockSharp size={20} />
          </span>
          <span className="nav-text">Document Access</span>
        </Link>

        <Link className="nav-link" to="/stock" onClick={handleNavClick}>
          <span className="nav-icon">
            <FaBoxesPacking size={20} />
          </span>
          <span className="nav-text">Stock</span>
        </Link>

        {/* Orders Dropdown */}
        <div className="nav-link dropdown" onClick={toggleOrdersDropdown}>
          <span className="nav-icon">
            <FaBoxes size={20} />
          </span>
          <span className="nav-text">Orders</span>
          <span className="nav-icon dropdown-icon">
            <FaChevronDown
              size={16}
              className={isOrdersDropdownOpen ? "rotate" : ""}
            />
          </span>
        </div>
        {isOrdersDropdownOpen && (
          <div className="dropdown-menu">
            <Link
              className="nav-link dropdown-item"
              to="/orders"
              onClick={handleNavClick}
            >
              <span className="nav-icon">
                <FaList size={20} />
              </span>
              <span className="nav-text">Orders List</span>
            </Link>
            <Link
              className="nav-link dropdown-item"
              to="/completed-order"
              onClick={handleNavClick}
            >
              <span className="nav-icon">
                <FaBox size={20} />
              </span>
              <span className="nav-text">Completed Orders</span>
            </Link>
            <Link
              className="nav-link dropdown-item"
              to="/add-order"
              onClick={handleNavClick}
            >
              <span className="nav-icon">
                <FaBoxOpen size={20} />
              </span>
              <span className="nav-text">Add Order</span>
            </Link>
          </div>
        )}

        <Link
          className="nav-link"
          to="/order-assignment"
          onClick={handleNavClick}
        >
          <span className="nav-icon">
            <FaFileAlt size={20} />
          </span>
          <span className="nav-text">Order Assignment</span>
        </Link>

        <div className="nav-link dropdown" onClick={toggleDropdown}>
          <span className="nav-icon">
            <FaRoute size={20} />
          </span>
          <span className="nav-text">Route Management</span>
          <span className="nav-icon dropdown-icon">
            <FaChevronDown
              size={16}
              className={isDropdownOpen ? "rotate" : ""}
            />
          </span>
        </div>
        {isDropdownOpen && (
          <div className="dropdown-menu">
            <Link
              className="nav-link dropdown-item"
              to="/route-results"
              onClick={handleNavClick}
            >
              <span className="nav-icon">
                <FaMapSigns size={20} />
              </span>
              <span className="nav-text">Route Results</span>
            </Link>
            <Link
              className="nav-link dropdown-item"
              to="/route-optimization"
              onClick={handleNavClick}
            >
              <span className="nav-icon">
                <FaRoute size={20} />
              </span>
              <span className="nav-text">Route Optimization</span>
            </Link>
            <Link
              className="nav-link dropdown-item"
              to="/cities"
              onClick={handleNavClick}
            >
              <span className="nav-icon">
                <FaCity size={20} />
              </span>
              <span className="nav-text">Cities</span>
            </Link>
            <Link
              className="nav-link dropdown-item"
              to="/vehicles"
              onClick={handleNavClick}
            >
              <span className="nav-icon">
                <FaCar size={20} />
              </span>
              <span className="nav-text">Vehicles</span>
            </Link>
          </div>
        )}
        <Link className="nav-link" to="/profile" onClick={handleNavClick}>
          <span className="nav-icon">
            <FaUser size={20} />
          </span>
          <span className="nav-text">Profile</span>
        </Link>
      </nav>
      <div className="sidebar-footer">
        <button
          className={`nav-link company-nav-btn ${
            isLoadingCompany ? "loading" : ""
          }`}
          onClick={() => {
            navigate("/profile");
            handleNavClick();
          }}
          disabled={isLoadingCompany}
          title="Go to Profile"
        >
          <span className="nav-icon">
            <FaWarehouse size={20} />
          </span>
          <span className="nav-text">
            {isLoadingCompany ? "Loading..." : companyName}
          </span>
        </button>
        <button className="glass-btn logout-btn" onClick={handleLogout}>
          <FaSignOutAlt size={20} />
          <span>Logout</span>
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
